import type { StaffNpcKind } from '../staffNpcs.js';

/** How an avatar interacts with a piece of furniture it stands on. */
export type SeatKind =
  /** Not a seat. */
  | 'none'
  /** A seat with no front (stools, bean bags): the sitter keeps their facing. */
  | 'free'
  /** A seat with a back: the sitter turns to the furniture's rotation. */
  | 'facing';

/** Shop sections. Each feature module files its items under one of these. */
export type FurnitureCategory =
  | 'kopitiam'
  /** Counters, stools and back-bar shelving for building a bar. */
  | 'bar'
  | 'seating'
  | 'decor'
  | 'singapore'
  | 'games'
  | 'work'
  | 'arcade'
  | 'pets'
  /** Running track and everything else for a race or a stadium scene. */
  | 'stadium'
  /** Pool water, coping and deck tiles to build a swimming pool. */
  | 'pool'
  | 'fun'
  /** Sofas, tables, pianos and other things for a living room. */
  | 'living'
  | 'bedroom'
  | 'kitchen'
  | 'bath'
  /** Walls, windows, doorways and pillars for building rooms inside a room. */
  | 'build'
  /** Lawns, paths, fences, trees, ponds and outdoor seating. */
  | 'garden'
  /** Crop beds, coops and hives that grow over the days (see garden.ts). */
  | 'farm'
  /** Sold only for Gold or Diamonds; see `premium`. */
  | 'rare'
  /** Lanterns, lights and table spreads for CNY, Hari Raya, Deepavali and Christmas. */
  | 'festive'
  /** Everyday recreational sports and gym equipment. */
  | 'sports'
  /** Hire-able NPCs that wander near the spot they were placed; see `spawnsStaff`. */
  | 'staff';

/**
 * What clicking the item does, beyond selecting it. Features register
 * handlers for these on both the client and the server.
 */
export type FurnitureInteraction =
  | { type: 'game'; game: string }
  | { type: 'job'; job: string }
  | { type: 'shop' }
  /** An arcade machine; `game` picks one mini-game, omitted shows a picker. */
  | { type: 'arcade'; game?: string }
  /** Pet shop counter, bowls, beds and toys; `action` says what it does. */
  | { type: 'pet'; action: string }
  /** A crop bed, coop or hive: opens the garden window for that spot. */
  | { type: 'garden' }
  /** Opens the jukebox's shared song queue. */
  | { type: 'jukebox' }
  /** A newsroom TV showing one channel (see news.ts). */
  | { type: 'news'; channel: 'updates' | 'stats' | 'events' }
  /** A door: click to open or close it. Open lets people walk through; closed blocks like a wall. */
  | { type: 'door' };

/** Animated effects a piece of furniture can carry (rare items). */
export type FurnitureEffectKind =
  /** Rising flames. */
  | 'flames'
  /** Twinkling stars around the piece. */
  | 'sparkle'
  /** Jets of water arcing out and falling back. */
  | 'spray'
  /** A pulsing halo with the odd spark. */
  | 'glow';

export interface FurnitureEffect {
  type: FurnitureEffectKind;
  /** Main colour, '#rrggbb'. */
  color: string;
  /** Second colour the particles fade towards (flames: the hot core); defaults to white. */
  color2?: string;
  /** Where the effect starts, in pixels above the tile's floor position. */
  height: number;
}

export interface FurnitureDef {
  kind: string;
  label: string;
  description: string;
  category: FurnitureCategory;
  /** Price in Kaki Dollars. Zero means not sold for Kaki Dollars. */
  price: number;
  /**
   * Sold for Gold or Diamonds instead. Such items have `price: 0`; players can still
   * trade or resell them, but the only way to get a new one is to pay in premium currency.
   */
  premium?: { currency: 'gold' | 'diamond'; price: number };
  /** Blocks pathfinding. */
  solid: boolean;
  seat: SeatKind;
  /** Standing next to it lets an avatar order a drink. */
  service: boolean;
  /** Visual height in tile-height units; also the seat height when sat on. */
  height: number;
  /** Standing on it makes an avatar swim (walking) or tread water (still). */
  water?: boolean;
  interaction?: FurnitureInteraction;
  /** A purely cosmetic animation drawn on top of the item. */
  effect?: FurnitureEffect;
  /** A time-boxed release: no cap on how many are sold, but sales stop at `endsAt` (ms since epoch). */
  limited?: { endsAt: number };
  /** Placing this spawns a wandering staff NPC anchored near it; picking it up removes the NPC. */
  spawnsStaff?: StaffNpcKind;
}

/** Whether a (possibly time-boxed) item can still be bought right now. */
export function isForSale(def: FurnitureDef, now = Date.now()): boolean {
  return !def.limited || now < def.limited.endsAt;
}

/** Shorthand for defining a catalogue section with sensible defaults. */
export function defineFurniture<const K extends string>(
  category: FurnitureCategory,
  items: Record<K, Omit<FurnitureDef, 'kind' | 'category' | 'seat' | 'service'> & Partial<Pick<FurnitureDef, 'seat' | 'service'>>>,
): Record<K, FurnitureDef> {
  const out = {} as Record<K, FurnitureDef>;
  for (const kind of Object.keys(items) as K[]) {
    const item = items[kind];
    out[kind] = { seat: 'none', service: false, ...item, kind, category };
  }
  return out;
}
